import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '../views/HomeView.vue'
import StaffDashboardView from '@/views/StaffDashboardView.vue'
import AdminProducts from '@/views/AdminProducts.vue'
import ManageAppProgressView from '@/views/ManageAppProgressView.vue'
import StaffManageSettingsView from '@/views/StaffManageSettingsView.vue'
import StaffOnboardCustomerView from '@/views/StaffOnboardCustomerView.vue'
import StaffProducts from '@/views/StaffProducts.vue'
import StaffProductDetail from '@/views/StaffProductDetail.vue'
import StaffPaymentsView from '@/views/StaffPaymentsView.vue'
import { PLACEHOLDER_PAGES } from '@/config/site'

Vue.use(VueRouter)

const ComingSoonView = () => import('../views/ComingSoonView.vue')

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/ResetPasswordView.vue'),
    meta: { title: 'Reset your password | Municipal Collectionz' }
  },
  {
    path: '/documentation',
    name: 'documentation',
    component: () => import('../views/DocumentationView.vue'),
    meta: { title: 'Documentation | Municipal Collectionz' }
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('../views/HelpCenterView.vue'),
    meta: { title: 'Help Center | Municipal Collectionz' }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/Privacy.vue'),
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('../views/Terms.vue'),
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: () => import('../views/Cookies.vue'),
  },
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { title: 'Municipal Collectionz | Every dollar your city is owed, collected in one place' }
  },
  {
    // Demo request form (saves to contact_requests)
    path: '/demo',
    name: 'demo',
    component: () => import('../views/DemoRequestView.vue'),
    meta: { title: 'Request a Demo | Municipal Collectionz' }
  },
  {
    path: '/why',
    name: 'why',
    component: () => import('../views/WhyView.vue'),
    meta: { title: 'Why Municipal Collectionz' }
  },
  {
    path: '/features',
    name: 'features',
    component: () => import('../views/FeaturesView.vue'),
    meta: { title: 'Features | Municipal Collectionz' }
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import('../views/BlogView.vue'),
    meta: { title: 'Blog | Municipal Collectionz' }
  },
  {
    path: '/blog/:slug',
    name: 'blog-post',
    component: () => import('../views/BlogPostView.vue')
  },
  // Marketing pages still to build: Documentation, Help, Contact, Security.
  // They show the "Coming soon" page. To build one, remove it from PLACEHOLDER_PAGES in
  // src/config/site.js and give it its own route here.
  ...PLACEHOLDER_PAGES.map((p) => ({
    path: p.path,
    name: p.name,
    component: ComingSoonView,
    props: { title: p.title, blurb: p.blurb },
    meta: { title: `${p.title} | Municipal Collectionz` }
  })),
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminDashboardView.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/admin/revenue',
    name: 'admin-revenue',
    component: () => import('../views/AdminRevenueReportsView.vue'),
    meta: { requiresAuth: true, role: 'Admin', title: 'Revenue | Municipal Collectionz' }
  },
  {
    // One city: rollout, contract, billing, files and requests
    path: '/admin/cities/:id',
    name: 'customer-detail',
    component: () => import('../views/CustomerDetailView.vue'),
    props: true,
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/admin/templates',
    name: 'admin-templates',
    component: () => import('../views/AdminTemplates.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/admin/products',
    name: 'AdminProducts',
    component: AdminProducts,
    meta: { requiresAuth: true, role: 'Admin' }
  },

  {
    path: '/manage-app-progress',
    name: 'ManageAppProgress',
    component: ManageAppProgressView,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin-settings',
    name: 'admin-settings',
    component: () => import('../views/AdminSettingsView.vue'),
    meta: { requiresAuth: true, role: 'Admin' }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import( '../views/CustomerDashboardView.vue'),
    meta: { requiresAuth: true, role: 'Customer' }
  },
  {
    path: '/dashboard/imports',
    name: 'customer-imports',
    component: () => import('../views/CustomerImportsView.vue'),
    meta: { requiresAuth: true, role: 'Customer', title: 'Upload files | Municipal Collectionz' }
  },
  {
    path: '/customer-settings',
    name: 'customer-settings',
    component: () => import('../views/CustomerSettingsView.vue'),
    meta: { requiresAuth: true, role: 'Customer' }
  },
  {
    path: '/staff-dashboard',
    name: 'StaffDashboard',
    component: StaffDashboardView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-onboard-customer',
    name: 'StaffOnboardCustomer',
    component: StaffOnboardCustomerView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-leads',
    name: 'StaffLeads',
    component: () => import('../views/StaffLeads.vue'),
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff/products',
    name: 'StaffProducts',
    component: StaffProducts,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff/products/:id',
    name: 'StaffProductDetail',
    component: StaffProductDetail,
    props: true,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    // Blog manager (staff and admins)
    path: '/staff/blog',
    name: 'staff-blog',
    component: () => import('../views/StaffBlogManageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/staff/templates',
    name: 'staff-templates',
    component: () => import('../views/StaffTemplates.vue'),
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/staff-payments',
    name: 'StaffPayments',
    component: StaffPaymentsView,
    meta: { requiresAuth: true, role: 'Staff' }
  },

  {
    path: '/staff-manage-settings',
    name: 'StaffManageSettings',
    component: StaffManageSettingsView,
    meta: { requiresAuth: true, role: 'Staff' }
  },
  {
    path: '/about',
    name: 'about',
    meta: { title: 'About | Municipal Collectionz' },
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: function () {
      return import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // New pages open at the top, #anchors scroll to their section, Back restores the position.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { selector: to.hash, offset: { x: 0, y: 80 } }
    return { x: 0, y: 0 }
  }
})

// Browser tab title for pages that set one.
router.afterEach((to) => {
  if (to.meta && to.meta.title) document.title = to.meta.title
})

export default router